/**
 * Hand-authored 8-bit sprites.
 *
 * Every sprite is a list of strings, one character per pixel, keyed to a fixed
 * palette. That is deliberate: a researcher reading this file can see the bird,
 * edit it, and know exactly what will be drawn, with no binary assets and no
 * questions about where the art came from.
 *
 * The art is original work for this project. It is drawn in the visual idiom of
 * the genre -- yellow bird, green pipes, striped ground -- but it is not, and
 * must not be replaced by, sprites ripped from Flappy Bird. Most of the
 * "CC0 flappy bird" packs in circulation are the original game's assets
 * re-uploaded by someone who does not own them.
 *
 * ## Resolution
 *
 * Sprites are authored at one art pixel per character and drawn into an
 * offscreen buffer at exactly that resolution, which is then upscaled with
 * nearest-neighbour filtering by an integer factor. Nothing is ever drawn at a
 * fractional art-pixel position, which is what keeps the chunky look honest
 * rather than blurry.
 */

/** Logical game pixels per art pixel. The whole 8-bit grid derives from this. */
export const ART_SCALE = 2;

export const PALETTE: Record<string, string> = {
  K: '#20201a', // outline
  Y: '#f8d850', // body
  O: '#e09028', // body shade / belly
  W: '#ffffff', // eye white
  w: '#f4f0dc', // wing
  v: '#cfc7a8', // wing shade
  B: '#f4802c', // beak
  b: '#c85c10', // beak shade
};

export type Sprite = {
  width: number;
  height: number;
  rows: string[];
};

function sprite(rows: string[]): Sprite {
  const width = rows[0].length;
  for (const r of rows) {
    if (r.length !== width) {
      throw new Error(`ragged sprite row: expected ${width}, got ${r.length} in "${r}"`);
    }
  }
  return { width, height: rows.length, rows };
}

/**
 * The bird, 17 x 12 art pixels, in three wing positions.
 *
 * Note the sprite is wider than the collision circle (radius 12 logical px =
 * 6 art px). The beak and tail overhang the hitbox, exactly as they do in the
 * original -- collision stays a circle on the body, and the renderer never
 * touches physics.
 */
/**
 * The bird, 21 x 15 art pixels, in three wing positions.
 *
 * The sprite is wider and taller than the collision circle (radius 12 logical
 * px = 6 art px): the beak, tail and wing overhang the hitbox, exactly as they
 * do in the original. Collision stays a circle on the body centre and the
 * renderer never touches physics — and because a sprite bigger than its hitbox
 * can mislead someone judging a near miss, the bot-vision overlay draws the
 * true collision circle on top.
 */
export const BIRD_FRAMES: Sprite[] = [
  // wing up
  sprite([
    '......KKKKKKK........',
    '....KKYYYYYYYKK......',
    '..KKKYYYYYYYYWWWKK...',
    '.KwwwKYYYYYYYWWWWKK..',
    'KwwwwwKYYYYYYWKKWWK..',
    'KwwwwwKYYYYYYWKKWWKKK',
    '.KwwwKYYYYYYYWWWWKBBB',
    '..KKKYYYYYYYYYKBBBBK.',
    '...KYYYYYYYYYYKBBBBK.',
    '...KYYYYYYYYYYKbbbK..',
    '..KKKYYYYYYYYYKKKK...',
    '...KYYYYYYYYYYYK.....',
    '...KOYYYYYYYYYK......',
    '....KOOOOOOOOK.......',
    '.....KKKKKKKK........',
  ]),
  // wing level
  sprite([
    '......KKKKKKK........',
    '....KKYYYYYYYKK......',
    '...KYYYYYYYYYWWWKK...',
    '..KYYYYYYYYYYWWWWKK..',
    '..KYYYYYYYYYYWKKWWK..',
    '.KYYYYYYYYYYYWKKWWKKK',
    '.KYYYYYYYYYYYWWWWKBBB',
    'KwwwwwKYYYYYYYYKBBBBK',
    'KwwwwwwKYYYYYYYKBBBBK',
    '.KwwwwKYYYYYYYYKbbbK.',
    '..KKKKKYYYYYYYYKKKK..',
    '...KYYYYYYYYYYYK.....',
    '...KOYYYYYYYYYK......',
    '....KOOOOOOOOK.......',
    '.....KKKKKKKK........',
  ]),
  // wing down
  sprite([
    '......KKKKKKK........',
    '....KKYYYYYYYKK......',
    '...KYYYYYYYYYWWWKK...',
    '..KYYYYYYYYYYWWWWKK..',
    '..KYYYYYYYYYYWKKWWK..',
    '.KYYYYYYYYYYYWKKWWKKK',
    '.KYYYYYYYYYYYWWWWKBBB',
    '.KYYYYYYYYYYYYYKBBBBK',
    '.KYYYYYYYYYYYYYKBBBBK',
    'KKKKYYYYYYYYYYYKbbbK.',
    'KvvvKKYYYYYYYYKKKK...',
    'KwwwwwKYYYYYYYK......',
    '.KwwwwKOYYYYYYK......',
    '..KwwKKOOOOOOK.......',
    '...KK..KKKKKK........',
  ]),
];

/** Baked sprites are cached: the same nine canvases serve every frame. */
const cache = new Map<Sprite, HTMLCanvasElement>();

/**
 * Rasterise a sprite to a 1:1 offscreen canvas.
 *
 * Written pixel by pixel into an ImageData rather than as fillRect calls, so
 * there is no chance of an antialiased edge sneaking into art that is supposed
 * to be hard-edged.
 */
export function bake(s: Sprite): HTMLCanvasElement {
  const cached = cache.get(s);
  if (cached) return cached;

  const canvas = document.createElement('canvas');
  canvas.width = s.width;
  canvas.height = s.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D context unavailable while baking a sprite');

  const image = ctx.createImageData(s.width, s.height);
  for (let y = 0; y < s.height; y++) {
    for (let x = 0; x < s.width; x++) {
      const ch = s.rows[y][x];
      if (ch === '.') continue;
      const hex = PALETTE[ch];
      if (!hex) throw new Error(`sprite uses '${ch}', which is not in the palette`);
      const i = (y * s.width + x) * 4;
      image.data[i] = parseInt(hex.slice(1, 3), 16);
      image.data[i + 1] = parseInt(hex.slice(3, 5), 16);
      image.data[i + 2] = parseInt(hex.slice(5, 7), 16);
      image.data[i + 3] = 255;
    }
  }
  ctx.putImageData(image, 0, 0);
  cache.set(s, canvas);
  return canvas;
}

/**
 * Pick a wing frame.
 *
 * The wing snaps to "up" the instant a flap is executed and then cycles down
 * over ~180 ms, so the animation is driven by the actual flap event rather than
 * by a free-running timer. In a mediated run that matters: the wing beats when
 * the participant's hand arrives, not when the bot decided it should.
 */
export function birdFrame(nowMs: number, lastFlapMs: number, alive: boolean): Sprite {
  if (!alive) return BIRD_FRAMES[2];
  const since = nowMs - lastFlapMs;
  if (since < 60) return BIRD_FRAMES[0];
  if (since < 120) return BIRD_FRAMES[1];
  if (since < 180) return BIRD_FRAMES[2];
  // Idle glide: a slow two-frame flutter so the bird is never a static block.
  return BIRD_FRAMES[Math.floor(nowMs / 220) % 2 === 0 ? 1 : 2];
}
